import { useQueryClient } from '@tanstack/react-query';
import { BreadcrumbItem } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CheckIcon from 'assets/images/icons/check.svg?react';
import Breadcrumb from 'components/breadcrumb/Breadcrumb';
import { getIntegrationAgencySettingsQueryKey } from 'hooks/useFetchIntegrationAgencySettings';
import useFormatCurrency from 'hooks/useFormatCurrency';
import { IntegrationEnum } from 'models/InternalIntegrationSetting';
import { INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY } from 'pages/channels/integrationSettingsQuery';
import { IntegrationMutationLevel } from 'pages/integrations/hooks/useDisableIntegrationMutation';
import useEnableIntegrationMutation from 'pages/integrations/hooks/useEnableIntegrationMutation';
import { HOSTFULLY_ASSIST_PATH } from '../HostfullyAssist.constants';
import AgreementsSection from './AgreementsSection';
import CoverageTable from './CoverageTable';
import {
  HOSTFULLY_ASSIST_CURRENCY,
  HOSTFULLY_ASSIST_PRICE_PER_PROPERTY,
  INCLUDED_ROWS,
} from './HostfullyAssistDetails.constants';
import {
  HostfullyAssistDetailsFormSchemaType,
  hostfullyAssistDetailsSchema,
} from './HostfullyAssistDetails.schema';
import {
  FormStyled,
  IncludedItem,
  IncludedTable,
  Page,
  PricingTable,
  TableScroller,
  TrialLabel,
} from './HostfullyAssistDetails.styles';
import useFetchAssistProperties from './useFetchAssistProperties';

const HostfullyAssistDetails = () => {
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { allProperties, isFetching: isFetchingProperties } =
    useFetchAssistProperties();
  const { enableIntegration, isLoading: isActivating } =
    useEnableIntegrationMutation();

  const defaultValues = {
    selectedProperties: allProperties?.length ? [allProperties[0].uid] : [],
  };

  const agencySettingsQueryKey = getIntegrationAgencySettingsQueryKey({
    enumId: IntegrationEnum.HOSTFULLYASSIST,
  });

  const handleSubmit = (values: HostfullyAssistDetailsFormSchemaType) => {
    enableIntegration(
      {
        level: IntegrationMutationLevel.PROPERTY,
        enumId: IntegrationEnum.HOSTFULLYASSIST,
        propertyUids: values.selectedProperties,
        appliesToAllProperties: false,
        applyToAgency: true,
        queryKey: agencySettingsQueryKey,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: agencySettingsQueryKey });
          // removeQueries (rather than invalidateQueries) so when the user navigates to the
          // integrations page the list can be re-fetched instead of seeing stale data
          queryClient.removeQueries({
            queryKey: [INTERNAL_INTEGRATION_SETTINGS_BASE_QUERY_KEY, 'list'],
          });
          navigate('/inbox');
        },
      },
    );
  };

  return (
    <Page data-testid="assist-details">
      <Breadcrumb>
        <BreadcrumbItem href={`#${HOSTFULLY_ASSIST_PATH}`}>
          {t('pageHostfullyAssist.details.breadcrumbs.hostfullyAssist')}
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t('pageHostfullyAssist.details.breadcrumbs.pricingAndSetup')}
        </BreadcrumbItem>
      </Breadcrumb>

      <TableScroller>
        <PricingTable data-testid="assist-details-pricing">
          <caption>{t('pageHostfullyAssist.details.pricing.caption')}</caption>
          <tbody>
            <tr>
              <td>
                {t(
                  'pageHostfullyAssist.details.pricing.perPropertySubscription',
                )}
              </td>
              <td>
                <TrialLabel>
                  {t('pageHostfullyAssist.details.pricing.trial')}
                </TrialLabel>
                {t('pageHostfullyAssist.details.pricing.price', {
                  price: formatCurrency({
                    value: HOSTFULLY_ASSIST_PRICE_PER_PROPERTY,
                    currency: HOSTFULLY_ASSIST_CURRENCY,
                  }),
                })}
              </td>
            </tr>
          </tbody>
        </PricingTable>
      </TableScroller>

      <TableScroller>
        <IncludedTable data-testid="assist-details-included">
          <caption>{t('pageHostfullyAssist.details.included.caption')}</caption>
          <tbody>
            {INCLUDED_ROWS.map(({ column1, column2 }) => (
              <tr key={column1.testId}>
                <td>
                  <IncludedItem data-testid={column1.testId}>
                    <CheckIcon aria-hidden />
                    {t(column1.labelKey)}
                  </IncludedItem>
                </td>
                <td>
                  <IncludedItem data-testid={column2.testId}>
                    <CheckIcon aria-hidden />
                    {t(column2.labelKey)}
                  </IncludedItem>
                </td>
              </tr>
            ))}
          </tbody>
        </IncludedTable>
      </TableScroller>

      <FormStyled
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        // Remount once the async property list arrives, so the default selection applies.
        key={JSON.stringify(defaultValues.selectedProperties)}
        schema={hostfullyAssistDetailsSchema()}
      >
        <CoverageTable
          isEditDisabled={isActivating || isFetchingProperties}
          properties={allProperties ?? []}
        />
        <AgreementsSection
          isActivating={isActivating}
          properties={allProperties ?? []}
        />
      </FormStyled>
    </Page>
  );
};

export default HostfullyAssistDetails;
